import { useTranslation } from 'react-i18next';
import { useModal } from '@/providers/modal';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { prettyMoney } from '@scramble/utils';
import { Badge } from '@scramble/ui';
import { EveryFiveGroupAModal } from '@/modals/EveryFiveGroupAModal';
import { useCalculateBonuses } from '@/containers/EveryFiveGroupA/useCalculateBonuses';
import { LAYOUT_TYPE } from './constants';
import Icon from '@scramble/design-system/icons/gift-01.svg';
import s from './main.module.scss';

// Cards
import { DashboardBonusCard } from '@/pages/DashboardPage/components/DashboardBonusCard';
import { BannerCard } from '@/containers/OffersBanner/card/BannerCard';
import { OfferCard } from '@/containers/OffersBanner/card/OfferCard';

export const EveryFiveGroupA = ({ data, amount, bonusAmount = 0, layout }) => {
  const { t } = useTranslation();
  const { bonus } = useCalculateBonuses(amount, bonusAmount);
  const { openSingleModal } = useModal();

  if (!data) return null;

  const props = {
    title: t('investing:every_five_conservative.get_bonus', {
      bonus_amount: prettyMoney(data.bonus_amount, { decimalPlaces: 0 }),
      investment_amount: prettyMoney(data.investment_amount, {
        decimalPlaces: 0,
      }),
    }),
    short_title: bonus
      ? t('investing:every_five_conservative.bonus_for_allocate', {
          amount: prettyMoney(bonus),
        })
      : t('investing:every_five_conservative.short_title', {
          bonus_amount: prettyMoney(data.bonus_amount, { decimalPlaces: 0 }),
          investment_amount: prettyMoney(data.investment_amount, {
            decimalPlaces: 0,
          }),
        }),
    desc: t('investing:every_five_conservative.maximize_earnings', {
      bonus_amount: prettyMoney(data.bonus_amount, { decimalPlaces: 0 }),
      investment_amount: prettyMoney(data.investment_amount, {
        decimalPlaces: 0,
      }),
    }),
    icon: Icon,
    modal: <EveryFiveGroupAModal />,
    onLearnMore: () => openSingleModal(MODAL_IDS.every_5_bonus_group_a),
  };

  const addMoreAmount = (num, base = 100) => {
    return num % base === 0 ? base : base - (num % base);
  };

  switch (layout) {
    case LAYOUT_TYPE.dashboard_desktop_card:
      return (
        <DashboardBonusCard
          title={props.short_title}
          text={props.desc}
          modal={props.modal}
          icon={<img src={Icon} alt="icon" width={24} height={24} />}
          footer={
            <button type="button" onClick={props.onLearnMore}>
              {t('investing:every_five_conservative.learn_more')}
            </button>
          }
        />
      );
    case LAYOUT_TYPE.investing_slider_card:
      return <BannerCard {...props} />;
    case LAYOUT_TYPE.investing_offer_card:
      return (
        <OfferCard {...props}>
          <>
            <div className={s.badge_extra}>
              {t('investing:every_five_conservative.add_more', {
                investment_amount: prettyMoney(
                  addMoreAmount(amount - bonusAmount, data.investment_amount), { decimalPlaces: 0}
                ),
              })}
            </div>
            <Badge
              type="neutral"
              appearance="filled"
              size="medium"
              label={prettyMoney(bonus, { decimalPlaces: 0 })}
            />
          </>
        </OfferCard>
      );
    default:
      return null;
  }
};
